import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { Calendar, Clock, Video, Phone, MapPin, CheckCircle, XCircle, RefreshCw, AlertCircle, ArrowRight, Download } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';

const API = '/api';

const INTERVIEW_TYPE_INFO = {
  online: { icon: Video, label: 'Online Meeting', color: 'text-blue-600' },
  phone: { icon: Phone, label: 'Phone Call', color: 'text-green-600' },
  onsite: { icon: MapPin, label: 'On-site Interview', color: 'text-orange-600' },
};

export default function InterviewResponsePage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [interview, setInterview] = useState(null);
  const [alreadyResponded, setAlreadyResponded] = useState(false);
  const [confirmSuccess, setConfirmSuccess] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [calendarUrl, setCalendarUrl] = useState(null);

  useEffect(() => {
    fetchInterviewDetails();
  }, [token]);

  const fetchInterviewDetails = async () => {
    try {
      const response = await fetch(`${API}/interview-response/${token}`);
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to load interview details';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          errorMessage = `Request failed: ${response.status}`;
        }
        throw new Error(errorMessage);
      }
      const data = await response.json();
      setInterview(data);
      
      // Check if already responded
      if (data.interview_schedule?.status && 
          ['confirmed', 'reschedule_requested', 'rescheduled'].includes(data.interview_schedule.status)) {
        setAlreadyResponded(true);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = async (slotIndex) => {
    setSubmitting(true);
    setSelectedSlot(slotIndex);
    try {
      const response = await fetch(`${API}/interview-response/${token}/confirm`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slot_index: slotIndex }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage = 'Failed to confirm interview';
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.detail || errorMessage;
        } catch {
          errorMessage = `Request failed: ${response.status}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      
      setConfirmSuccess(true);
      
      // Set calendar download URL
      if (result.calendar_download_url) {
        setCalendarUrl(result.calendar_download_url);
      }
      
      toast.success('Interview confirmed successfully!');
    } catch (err) {
      toast.error(err.message || 'An unexpected error occurred');
      setSelectedSlot(null);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestReschedule = () => {
    navigate(`/interview-response/${token}/reschedule`);
  };

  const formatDateTime = (date, time) => {
    if (!date) return 'Date not specified';
    const d = new Date(date);
    const options = { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' };
    const dateStr = d.toLocaleDateString('en-US', options);
    return time ? `${dateStr} at ${time}` : dateStr;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e0f2fe] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin text-[#33AADD]" />
            <p className="text-gray-600">Loading interview details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e0f2fe] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <XCircle className="h-12 w-12 mx-auto mb-4 text-red-500" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Unable to Load Interview</h2>
            <p className="text-gray-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (confirmSuccess) {
    const confirmedSlot = interview.interview_schedule.proposed_slots[selectedSlot];
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f0fdf4] to-[#dcfce7] flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-green-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 mx-auto mb-4 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle className="h-10 w-10 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-green-800 mb-2">Interview Confirmed!</h2>
            <p className="text-green-700 mb-6">
              Your interview for <strong>{interview.job_title}</strong> has been scheduled.
            </p>
            <div className="bg-white border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-500 mb-1">Confirmed Time:</p>
              <p className="text-lg font-semibold text-gray-800">
                {formatDateTime(confirmedSlot?.date, confirmedSlot?.time)}
              </p>
            </div>
            
            {/* Calendar Download Button */}
            {calendarUrl && (
              <a
                href={calendarUrl}
                download
                className="inline-flex items-center justify-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white font-medium py-3 px-4 rounded-lg transition-colors mb-4"
                data-testid="download-calendar-btn"
              >
                <Download className="h-5 w-5" />
                Add to Calendar (.ics)
              </a>
            )}
            
            <p className="text-sm text-gray-600">
              The recruiter has been notified. You will receive a confirmation email shortly.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (alreadyResponded) {
    const schedule = interview.interview_schedule;
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e0f2fe] flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <AlertCircle className="h-12 w-12 mx-auto mb-4 text-amber-500" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">Already Responded</h2>
            <p className="text-gray-600 mb-4">
              You have already responded to this interview invitation.
            </p>
            {schedule.status === 'confirmed' && schedule.confirmed_slot && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-sm text-green-700 font-medium">Interview Confirmed:</p>
                <p className="text-green-800">
                  {formatDateTime(schedule.confirmed_slot.date, schedule.confirmed_slot.time)}
                </p>
                <a
                  href={`/api/interview-response/${token}/calendar.ics`}
                  download
                  className="inline-flex items-center gap-1 text-sm text-green-600 hover:text-green-700 mt-2"
                >
                  <Download className="h-4 w-4" />
                  Add to Calendar
                </a>
              </div>
            )}
            {schedule.status === 'reschedule_requested' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm text-amber-700">
                  You have requested a different time. The recruiter will contact you soon.
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  const schedule = interview.interview_schedule;
  const TypeInfo = INTERVIEW_TYPE_INFO[schedule.interview_type] || INTERVIEW_TYPE_INFO.online;
  const TypeIcon = TypeInfo.icon;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#f8fafc] to-[#e0f2fe] py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold text-[#33AADD] mb-1">WeDo HR Services</h1>
          <p className="text-gray-600">Interview Scheduling</p>
        </div>

        {/* Main Card */}
        <Card className="shadow-lg" data-testid="interview-response-card">
          <CardHeader className="bg-gradient-to-r from-[#33AADD] to-[#2890BD] text-white rounded-t-lg">
            <CardTitle className="text-xl">Interview Invitation</CardTitle>
            <CardDescription className="text-white/90">
              {interview.job_title}
            </CardDescription>
          </CardHeader>

          <CardContent className="p-6 space-y-6">
            {/* Candidate Greeting */}
            <div>
              <p className="text-gray-700">
                Hello <strong>{interview.candidate_name}</strong>,
              </p>
              <p className="text-gray-600 mt-2 text-sm">
                You have been invited for an interview. Please select one of the proposed time slots or request a different time.
              </p>
            </div>

            {/* Interview Type */}
            <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
              <TypeIcon className={`h-6 w-6 ${TypeInfo.color}`} />
              <div>
                <p className="font-medium text-gray-800">{TypeInfo.label}</p>
                {schedule.interview_type === 'online' && schedule.meeting_link && (
                  <a 
                    href={schedule.meeting_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-[#33AADD] hover:underline"
                  >
                    Meeting link will be shared upon confirmation
                  </a>
                )}
                {schedule.interview_type === 'onsite' && schedule.location && (
                  <p className="text-sm text-gray-600">{schedule.location}</p>
                )}
              </div>
            </div>

            {/* Time Options */}
            <div className="space-y-3">
              <p className="font-medium text-gray-800 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-[#33AADD]" />
                Available Time Slots
              </p>
              
              {schedule.proposed_slots.map((slot, index) => (
                <div 
                  key={index}
                  className="border rounded-lg p-4 hover:border-[#33AADD] transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{slot.label || `Option ${index + 1}`}</p>
                      <p className="font-semibold text-gray-800 flex items-center gap-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        {formatDateTime(slot.date, slot.time)}
                      </p>
                    </div>
                    <Button
                      onClick={() => handleConfirm(index)}
                      disabled={submitting}
                      className="bg-green-600 hover:bg-green-700"
                      data-testid={`confirm-slot-${index}-btn`}
                    >
                      {submitting && selectedSlot === index ? (
                        <RefreshCw className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <CheckCircle className="h-4 w-4 mr-1" />
                          Confirm
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>

            {/* Additional Notes */}
            {schedule.notes && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm font-medium text-amber-800 mb-1">Note from recruiter:</p>
                <p className="text-sm text-amber-700">{schedule.notes}</p>
              </div>
            )}

            {/* Reschedule Option */}
            <div className="pt-4 border-t">
              <p className="text-sm text-gray-600 mb-3 text-center">
                None of these times work for you?
              </p>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleRequestReschedule}
                data-testid="request-reschedule-btn"
              >
                Request a Different Time
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-gray-500 mt-6">
          © {new Date().getFullYear()} WeDo HR Services UG • Germany
        </p>
      </div>
    </div>
  );
}
